import React, { useEffect, useState } from 'react';
import {
  ClockOutlined,
  DiscountUniversal,
  MapOutlined,
  SubtractUniversal,
  UserGroupOutlined,
} from '@duxton-web/icons';
import { Tooltip, Typography } from '@duxton-web/ui';
import { Spin, Tabs, TabsProps } from 'antd';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';

import Service from '../../../lib/App';
import { AdaptiveExperiment, Experiment } from '../../../lib/PlannerDTOs';
import { appendEnvParam, useAppNavigate } from '../../../utils/navigation';
import {
  EXPERIMENT_FILTER_QUERY_SS_KEY,
  ExperimentAssignmentStrategy,
  SELECTED_EXPERIMENT_LS_KEY,
} from '../../ExperimentFormV2/helper/consts';
import AssignmentStrategyOption, {
  getTimeSliceStrategyTitle,
} from '../../ExperimentFormV2/Sections/Treatment/AssignmentStrategy/AssignmentStrategyOption';
import { AdaptiveDetailDrawer } from '../AdaptiveDetailSubPage/AdaptiveDetail';
import ExperimentDetailFooter from './DetailFooter';
import ExperimentDetailApprovalFlow from './ExperimentDetailApprovalFlow';
import ExperimentDetailOverlap from './ExperimentDetailOverlap';
import ExperimentDetailToggleView from './ExperimentDetailToggleView';

import './index.css';

import { PageHeaderInfo } from '@web-platform/page-header';
import { useLocalStorage } from 'react-use';

import { getStatusTagConfig } from '../common/DashboardColumn';
import { ObjectDetailPage } from '@/common/components';
import { ABIcon } from '@/common/Icons/ABIcon';

export const TIMESTAMP_FORMAT = 'MMM DD, YYYY HH:mm ZZ';

function ExperimentDetailSubPage({ exp }: { exp?: Experiment }) {
  const { navigateTo } = useAppNavigate();
  const { experimentName, tabName } = useParams<{
    experimentName?: string;
    tabName?: string;
  }>();
  const currentTabName = tabName || 'overview';
  const [_selectedExperiment, setSelectedExperiment] =
    useLocalStorage<Experiment>(SELECTED_EXPERIMENT_LS_KEY);
  const [experiment, setExperiment] = useState<Experiment>(exp);
  const [adaptiveExperiment, setAdaptiveExperiment] =
    React.useState<AdaptiveExperiment>(null);

  const enrichExperiment = (experiment: Experiment) => {
    const variables = experiment?.variables || [];

    variables.forEach((variable) => {
      const strategy = variable.strategy;

      if (
        [
          ExperimentAssignmentStrategy.GEO_TIME_SLICE_SHUFFLE,
          ExperimentAssignmentStrategy.TIME_SLICE,
          ExperimentAssignmentStrategy.TIME_SLICE_V2,
          ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE,
          ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
        ].includes(strategy)
      ) {
        const choices = variable.choices || [];

        choices.forEach((choice) => {
          choice.span = choice.span || 0;
          choice.spanBuffer = choice.spanBuffer || 0;
        });
      }
    });

    return experiment;
  };

  useEffect(() => {
    if (exp) return;
    if (!experimentName) return;
    Service.planner
      .fetchExperimentViewsV2({ name: experimentName })
      .then((results) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { experiment_views } = results;
        const record = experiment_views?.find((experiment_view) => {
          return (
            experiment_view?.adaptive_experiment_model?.name ===
              experimentName ||
            experiment_view?.experiment_model?.name === experimentName
          );
        });

        if (!record) return;

        const experiment = record.experiment_model;

        if (experiment) {
          const startTime = record.start_time;
          experiment['startTime'] = startTime;
        }

        const adaptiveExperiment = record.adaptive_experiment_model;
        setExperiment(enrichExperiment(experiment));
        setSelectedExperiment(experiment);
        setAdaptiveExperiment(adaptiveExperiment);
      });
  }, [exp, experimentName]);

  function getExperimentInfo(): PageHeaderInfo[] {
    let infos = [
      {
        title: 'Created by',
        content: experiment?.owner,
      },
      {
        title: 'Type',
        content: 'A/B test',
      },
      {
        title: 'Domain',
        content: experiment?.domain,
      },
    ];

    if (experiment?.metadata?.productManager)
      infos.push({
        title: 'Product manager',
        content: experiment?.metadata?.productManager,
      });

    if (experiment?.metadata.productAnalyst)
      infos.push({
        title: 'Product analyst',
        content: experiment?.metadata.productAnalyst,
      });

    return infos;
  }

  const items: TabsProps['items'] = [
    {
      key: '0',
      label: '',
    },
    {
      key: 'overview',
      label: 'Setup',
      children: <ExperimentDetailToggleView experiment={experiment} />,
    },
    {
      key: 'overlap',
      label: 'Overlapping',
      children: <ExperimentDetailOverlap experiment={experiment} />,
    },
    {
      key: 'approval',
      label: 'Approval Flow',
      children: <ExperimentDetailApprovalFlow experiment={experiment} />,
    },
  ];

  if (adaptiveExperiment) {
    return <AdaptiveDetailDrawer adaptiveExperiment={adaptiveExperiment} />;
  } else if (experiment) {
    const filterQuery = sessionStorage.getItem(EXPERIMENT_FILTER_QUERY_SS_KEY);
    const { color, content, tooltip } = getStatusTagConfig(experiment?.state);

    return (
      <ObjectDetailPage
        icon={<ABIcon />}
        breadcrumbs={[
          {
            path: filterQuery
              ? appendEnvParam(`/t/experiments?${filterQuery}`)
              : appendEnvParam(`/t/experiments`),
            onClick: () => {
              sessionStorage.removeItem(EXPERIMENT_FILTER_QUERY_SS_KEY);
            },
            title: 'Experiment',
          },
          {
            path: '',
            title: experiment?.name,
          },
        ]}
        descriptionLabel="Hypothesis"
        description={
          experiment?.metadata?.description ? (
            <Tooltip
              title={experiment.metadata.description}
              overlayStyle={{ maxWidth: 600 }}
            >
              <Typography.Text
                ellipsis
                style={{ maxWidth: '60vw', color: '#A3A3A3', fontSize: 12 }}
              >
                {experiment.metadata.description}
              </Typography.Text>
            </Tooltip>
          ) : (
            <SubtractUniversal />
          )
        }
        infos={getExperimentInfo()}
        footer={[
          <ExperimentDetailFooter
            key="experiment-detail-sub-page-footer"
            experiment={experiment}
          />,
        ]}
        className="experimentPageV2-subPage"
        headerTags={[
          {
            color: color,
            content: content,
            tooltip: tooltip,
            bordered: false,
            size: 'xsmall',
          },
        ]}
      >
        <Tabs
          id="experiment-detail-tabs"
          tabBarStyle={{
            position: 'sticky',
            top: 0,
            zIndex: 3,
            marginBottom: 0,
            background: '#fff',
          }}
          defaultActiveKey={currentTabName}
          activeKey={currentTabName}
          items={items}
          onTabClick={(key) =>
            navigateTo(`/t/experiments/${experimentName}/${key}`)
          }
        />
      </ObjectDetailPage>
    );
  }

  return <Spin />;
}

export const convertUnixDayJS = (value: string) => {
  return dayjs.unix(parseInt(value, 10)).format(TIMESTAMP_FORMAT);
};

export function assignStrategyOption(strategy: string) {
  let metadata = { icon: null, title: '', subtitle: '' };

  switch (strategy) {
    case ExperimentAssignmentStrategy.UNIFORM_CHOICE:
    case ExperimentAssignmentStrategy.WEIGHTED_CHOICE:
      metadata = {
        icon: <DiscountUniversal />,
        title: 'Percentage Split',
        subtitle: 'Each bucket has a same or different weight.',
      };
      break;
    case ExperimentAssignmentStrategy.TIME_SLICE:
    case ExperimentAssignmentStrategy.TIME_SLICE_V2:
    case ExperimentAssignmentStrategy.TIME_SLICE_GROUPED:
    case ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE:
    case ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE:
      metadata = {
        icon: <ClockOutlined />,
        title: getTimeSliceStrategyTitle(strategy),
        subtitle: 'Specify a period of time for each variant.',
      };
      break;
    case ExperimentAssignmentStrategy.GEO_TIME_SLICE_SHUFFLE:
      metadata = {
        icon: <MapOutlined />,
        title: 'Geo Time Slice Shuffle',
        subtitle: 'Shuffle based on geo regions',
      };
      break;
    case ExperimentAssignmentStrategy.EXPLICIT_GROUP:
      metadata = {
        icon: <UserGroupOutlined />,
        title: 'Explicit Group',
        subtitle: 'Specify a group for each variant.',
      };
      break;
    default:
      metadata = {
        icon: <UserGroupOutlined />,
        title: 'Unsupported assignment strategy',
        subtitle: 'Unsupported assignment strategy',
      };
  }

  return (
    <AssignmentStrategyOption
      disabled
      title={metadata.title}
      subtitle={metadata.subtitle}
      selected={[...Object.values(ExperimentAssignmentStrategy)].includes(
        strategy,
      )}
      icon={metadata.icon}
    />
  );
}

export default ExperimentDetailSubPage;
